import React from "react";
import {
  Modal,
  Form,
  InputNumber,
  Select,
  Input,
  Space,
  Typography,
  Tag,
} from "antd";
import { Bill, UpdateBillRequest } from "../../types/Billing";

const { Option } = Select;
const { TextArea } = Input;
const { Text, Title } = Typography;

interface UpdatePaymentModalProps {
  bill: Bill;
  visible: boolean;
  onClose: () => void;
  onUpdate: (billId: string, updateData: UpdateBillRequest) => void;
}

const UpdatePaymentModal: React.FC<UpdatePaymentModalProps> = ({
  bill,
  visible,
  onClose,
  onUpdate,
}) => {
  // Handle both cases: patient as string ID or as populated Patient object
  const patientData =
    typeof bill.patient === "string" ? bill.patientDetails : bill.patient;

  const [form] = Form.useForm();

  const getStatusColor = (status: string) => {
    switch (status) {
      case "paid":
        return "success";
      case "partially_paid":
        return "warning";
      case "pending":
        return "default";
      case "refunded":
        return "processing";
      default:
        return "default";
    }
  };

  const handleSubmit = (values: any) => {
    const updateData: UpdateBillRequest = {
      amountPaid: values.amountPaid,
      paymentMethod: values.paymentMethod,
      paymentStatus: values.paymentStatus,
      notes: values.notes,
    };

    onUpdate(bill._id, updateData);
  };

  const calculateNewBalance = (amountPaid: number) => {
    return Math.max(0, bill.grandTotal - amountPaid);
  };

  const getCurrentBalance = () => {
    return calculateNewBalance(
      form.getFieldValue("amountPaid") || bill.amountPaid
    );
  };

  const suggestPaymentStatus = (amountPaid: number) => {
    if (amountPaid >= bill.grandTotal) {
      return "paid";
    } else if (amountPaid > 0) {
      return "partially_paid";
    } else {
      return "pending";
    }
  };

  return (
    <Modal
      title="Update Payment"
      open={visible}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Update Payment"
      width={600}
    >
      {/* Bill Summary */}
      <div
        style={{
          background: "#fafafa",
          padding: "16px",
          borderRadius: "8px",
          marginBottom: "24px",
        }}
      >
        <Title level={5} style={{ margin: 0, marginBottom: "12px" }}>
          Bill Summary - {bill.billNumber}
        </Title>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text>Patient:</Text>
            <Text strong>{patientData?.name || "N/A"}</Text>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text>Grand Total:</Text>
            <Text strong>₹{bill.grandTotal.toFixed(2)}</Text>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text>Amount Paid:</Text>
            <Text style={{ color: "#52c41a" }}>
              ₹{bill.amountPaid.toFixed(2)}
            </Text>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text>Current Balance:</Text>
            <Text style={{ color: bill.balance > 0 ? "#ff4d4f" : "#52c41a" }}>
              ₹{bill.balance.toFixed(2)}
            </Text>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text>Status:</Text>
            <Tag color={getStatusColor(bill.paymentStatus)}>
              {bill.paymentStatus.charAt(0).toUpperCase() +
                bill.paymentStatus.slice(1)}
            </Tag>
          </div>
        </Space>
      </div>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          amountPaid: bill.amountPaid,
          paymentMethod: bill.paymentMethod,
          paymentStatus: bill.paymentStatus,
          notes: bill.notes,
        }}
        onFinish={handleSubmit}
      >
        <Form.Item
          label="Amount Paid"
          name="amountPaid"
          rules={[
            { required: true, message: "Please enter amount paid" },
            { type: "number", min: 0, message: "Amount must be positive" },
          ]}
        >
          <InputNumber
            min={0}
            max={bill.grandTotal}
            step={0.01}
            precision={2}
            style={{ width: "100%" }}
            addonBefore="₹"
            onChange={(value) => {
              if (value !== null) {
                const suggestedStatus = suggestPaymentStatus(value);
                form.setFieldValue("paymentStatus", suggestedStatus);
              }
            }}
          />
        </Form.Item>

        {/* New Balance Display */}
        <div
          style={{
            background: "#f0f2ff",
            padding: "12px",
            borderRadius: "6px",
            marginBottom: "16px",
            border: "1px solid #d6e4ff",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text>New Balance:</Text>
            <Text
              strong
              style={{
                fontSize: "16px",
                color: getCurrentBalance() > 0 ? "#ff4d4f" : "#52c41a",
              }}
            >
              ₹{getCurrentBalance().toFixed(2)}
            </Text>
          </div>
        </div>

        <Form.Item
          label="Payment Method"
          name="paymentMethod"
          rules={[{ required: true, message: "Please select payment method" }]}
        >
          <Select>
            <Option value="cash">Cash</Option>
            <Option value="card">Card</Option>
            <Option value="upi">UPI</Option>
            <Option value="net_banking">Net Banking</Option>
            <Option value="insurance">Insurance</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Payment Status"
          name="paymentStatus"
          rules={[{ required: true, message: "Please select payment status" }]}
        >
          <Select>
            <Option value="pending">Pending</Option>
            <Option value="partially_paid">Partially Paid</Option>
            <Option value="paid">Paid</Option>
            <Option value="refunded">Refunded</Option>
          </Select>
        </Form.Item>

        <Form.Item label="Notes" name="notes">
          <TextArea rows={3} placeholder="Add payment notes or remarks..." />
        </Form.Item>

        {/* Quick Payment Options */}
        <div style={{ marginBottom: "16px" }}>
          <Text strong>Quick Payment Options:</Text>
          <div style={{ marginTop: "8px" }}>
            <Space wrap>
              <Tag
                style={{ cursor: "pointer" }}
                onClick={() => {
                  form.setFieldValue("amountPaid", bill.grandTotal);
                  form.setFieldValue("paymentStatus", "paid");
                }}
              >
                Pay Full Amount (₹{bill.grandTotal.toFixed(2)})
              </Tag>
              {bill.balance > 0 && (
                <Tag
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    const newAmount = bill.amountPaid + bill.balance / 2;
                    form.setFieldValue("amountPaid", newAmount);
                    form.setFieldValue(
                      "paymentStatus",
                      newAmount >= bill.grandTotal ? "paid" : "partially_paid"
                    );
                  }}
                >
                  Pay Half Balance (₹{(bill.balance / 2).toFixed(2)})
                </Tag>
              )}
            </Space>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default UpdatePaymentModal;
